import PropTypes from 'prop-types';
import React from 'react';

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import classnames from 'classnames';

import { useScreenWrapper } from '@ezbobdev/ui-commons';

import MaterialContinue from '../MaterialContinue/MaterialContinue';
import MaterialProgressBar from '../MaterialProgressBar/MaterialProgressBar';
import MaterialScrollIndicator from '../MaterialScrollIndicator/MaterialScrollIndicator';
import MaterialSwipeableDrawer from '../MaterialSwipeableDrawer/MaterialSwipeableDrawer';
import MaterialTypography from '../MaterialTypography/MaterialTypography';
import {
  SCREEN_VARIANT,
  ScreenWrapperActions,
  ScreenWrapperDisclaimer,
  ScreenWrapperDrawerWrapper,
  VARIANTS,
  Wrapper,
} from './ScreenWrapper.styled';

const ScreenWrapper = (props) => {
  const {
    children,
    title,
    titleVariant = 'h1',
    subtitle,
    disclaimer,
    disclaimerComponent,
    actions,
    backgroundImage = {},
    subtitleVariant = 'body1',
    actionsVariant = 'static',
    isFinalScreen = false,
    variant = 'standard',
    maxWidth = '66.5rem',
    fullWidth = false,
    contentFullWidth = false,
    className = '',
    onClickBack,
    backButtonLabel,
    customHeader,
    screenWrapperBackgroundImage,
    progressBarImage,
    footer,
    showBackArrow,
  } = props;

  const {
    centeredElement,
    drawerContentExists,
    drawerName,
    hasBackButton,
    isAppBarHidden,
    progressBarStep,
    scrollAnchorRef,
    shouldShowProgressBar,
    shouldShowScrollIndicator,
    t,
    wrapperTopImageAlt,
    wrapperTopImageSrc,
    CENTERED_ELEMENT,
  } = useScreenWrapper(props);

  const computedVariant = progressBarStep ? VARIANTS.progressBar : variant;

  return (
    <Wrapper
      className={classnames(ScreenWrapper.displayName, className)}
      $hasBackButton={hasBackButton}
      $fullWidth={fullWidth}
      $maxWidth={maxWidth}
      variant={computedVariant}
      $contentFullWidth={contentFullWidth}
      $isFinalScreen={isFinalScreen}
      $backgroundImage={backgroundImage}
      $screenWrapperBackgroundImage={screenWrapperBackgroundImage}
      $isAppBarHidden={isAppBarHidden}
    >
      <div className={classnames(`${ScreenWrapper.displayName}__inner`)}>
        {shouldShowProgressBar && (
          <div className={classnames(`${ScreenWrapper.displayName}__progress-bar`)}>
            {progressBarImage?.desktop && (
              <img
                className={`${ScreenWrapper.displayName}__progress-bar-background-image`}
                src={progressBarImage.desktop}
                alt=""
              />
            )}
            <MaterialProgressBar name="progressBar" />
          </div>
        )}

        <div className={classnames(`${ScreenWrapper.displayName}__inner-container`, computedVariant)}>
          {hasBackButton && (
            <div className={classnames(`${ScreenWrapper.displayName}__top-bar`)}>
              <MaterialContinue
                data-testid="back-button"
                className="MaterialBack"
                variant="text"
                size="large"
                direction="BACKWARD"
                disableRipple
                shouldUpdate={false}
                shouldValidate={() => false}
                shouldMoveToStep={onClickBack}
                fullWidth={false}
                stateDriven
              >
                {showBackArrow && <KeyboardArrowRightIcon />}
                {backButtonLabel || t('globals.back')}
              </MaterialContinue>
            </div>
          )}

          {wrapperTopImageSrc && (
            <img
              className={`${ScreenWrapper.displayName}__top-image`}
              src={wrapperTopImageSrc}
              alt={wrapperTopImageAlt}
              fetchPriority="high"
            />
          )}

          {customHeader && (
            <header
              className={classnames(`${ScreenWrapper.displayName}__custom-header`, {
                centered: centeredElement === CENTERED_ELEMENT.CUSTOM_HEADER,
              })}
            >
              {customHeader}
            </header>
          )}

          {(title || subtitle) && (
            <header
              className={classnames(`${ScreenWrapper.displayName}__header`, {
                centered: centeredElement === CENTERED_ELEMENT.HEADER,
              })}
            >
              {title && (
                <MaterialTypography
                  className={classnames(`${ScreenWrapper.displayName}__header__title`)}
                  variant={titleVariant}
                  id="page-header"
                >
                  {title}
                </MaterialTypography>
              )}

              {subtitle && (
                <MaterialTypography
                  className={classnames(
                    `${ScreenWrapper.displayName}__header__subtitle`,
                    isFinalScreen ? 'MuiTypography-h2--big' : '',
                  )}
                  variant={subtitleVariant}
                  id="page-subtitle"
                >
                  {subtitle}
                </MaterialTypography>
              )}
            </header>
          )}

          <div
            className={classnames(`${ScreenWrapper.displayName}__content`, {
              centered: centeredElement === CENTERED_ELEMENT.CONTENT,
            })}
          >
            {children}
          </div>

          {(actions || disclaimer) && (
            <ScreenWrapperActions
              className={classnames(`${ScreenWrapper.displayName}__actions`)}
              $actionsVariant={actionsVariant}
              ref={!drawerContentExists ? scrollAnchorRef : null}
            >
              {disclaimer && (
                <ScreenWrapperDisclaimer
                  className={classnames(`${ScreenWrapper.displayName}__disclaimer`)}
                  variant="body2"
                  component={disclaimerComponent}
                >
                  {disclaimer}
                </ScreenWrapperDisclaimer>
              )}
              {actions}
            </ScreenWrapperActions>
          )}

          {footer && <div className={classnames(`${ScreenWrapper.displayName}__footer`)}>{footer}</div>}
        </div>

        {drawerContentExists && (
          <ScreenWrapperDrawerWrapper ref={scrollAnchorRef}>
            <MaterialSwipeableDrawer name={drawerName} />
          </ScreenWrapperDrawerWrapper>
        )}
      </div>

      {shouldShowScrollIndicator && (
        <MaterialScrollIndicator thresholdElement={scrollAnchorRef.current?.lastElementChild} />
      )}
    </Wrapper>
  );
};

ScreenWrapper.displayName = 'ScreenWrapper';

ScreenWrapper.propTypes = {
  children: PropTypes.node,
  fullWidth: PropTypes.bool,
  maxWidth: PropTypes.string,
  contentFullWidth: PropTypes.bool,
  className: PropTypes.string,
  disclaimer: PropTypes.string,
  disclaimerComponent: PropTypes.string,
  actions: PropTypes.node,
  variant: PropTypes.oneOf(Object.keys(SCREEN_VARIANT)),
  actionsVariant: PropTypes.oneOf(['static', 'fixed']),
  backgroundImage: PropTypes.shape({
    desktop: PropTypes.string,
    mobile: PropTypes.string,
  }),
  screenWrapperBackgroundImage: PropTypes.shape({
    desktop: PropTypes.string,
    mobile: PropTypes.string,
  }),
  progressBarImage: PropTypes.shape({
    desktop: PropTypes.string,
  }),
  title: PropTypes.string,
  titleVariant: PropTypes.string,
  subtitle: PropTypes.node,
  customHeader: PropTypes.node,
  isFinalScreen: PropTypes.bool,
  onClickBack: PropTypes.func,
  backButtonLabel: PropTypes.string,
  topImage: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  subtitleVariant: PropTypes.string,
  showBackArrow: PropTypes.bool,
};

export default ScreenWrapper;
